import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { formatDateMsDsYHI } from '@helpers/formatDateMsDsYHI';
import { useAuthContext } from '@providers/AuthProvider';
import { useAPI } from '@providers/useAPI';
import { type JobDtoAssigneeHistory, JobsService, type JobStatusHistoryItemDto } from 'app/API';
import { JOB_STATUSES_ENUM } from 'app/types/enums/jobStatuses';
import React from 'react';
import { Content } from './styles/Content';
import {
  LeftTabButton,
  RightTabButton,
} from '@components/core-ui/form-elements/buttons/Button/variations/JobHistoryTabButtons';

type TableRow = JobStatusHistoryItemDto | JobDtoAssigneeHistory;

type TProps = {
  onCancel: () => void;
  jobId: string;
  workspaceId?: string;
};

export const JobHistory = ({ onCancel, jobId, workspaceId }: TProps) => {
  const { call } = useAPI();
  const { workspace } = useAuthContext();
  const [currentTab, setCurrentTab] = React.useState<'Status' | 'Assignee'>('Status');
  const [data, setData] = React.useState<{
    status: JobStatusHistoryItemDto[];
    assignee: JobDtoAssigneeHistory[];
  }>({ status: [], assignee: [] });

  const jobWorkspaceId = workspaceId ?? workspace?.id ?? '';

  React.useEffect(() => {
    (async () => {
      const statuses = await call(JobsService.getJobStatusHistory({ wsid: jobWorkspaceId, jobId }));
      const assignees = await call(JobsService.getJobAssigneeHistory({ wsid: jobWorkspaceId, jobId }));
      setData({
        status: statuses.result ?? [],
        assignee: assignees.result ?? [],
      });
    })();
  }, []);

  return (
    <ModalWrapper width="480px">
      <ModalTitle>History</ModalTitle>
      <Content>
        <div className="switcher">
          <LeftTabButton
            className="w-full"
            $active={currentTab === 'Status'}
            type="button"
            onClick={() => setCurrentTab('Status')}
          >
            Status
          </LeftTabButton>
          <RightTabButton
            className="w-full"
            $active={currentTab === 'Assignee'}
            type="button"
            onClick={() => setCurrentTab('Assignee')}
          >
            Assignee
          </RightTabButton>
        </div>

        <TableWrapper className="customTable">
          <Table>
            <Thead $white>
              <Tr>
                <Th>Date</Th>
                <Th>{currentTab === 'Status' ? 'Status' : 'Assignee'}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(currentTab === 'Status' ? data.status : data.assignee).map((row: TableRow, index) => {
                let contentVal;
                if (currentTab === 'Status') {
                  const s = row as JobStatusHistoryItemDto;
                  contentVal = <p>{JOB_STATUSES_ENUM[(s.status ?? '') as keyof typeof JOB_STATUSES_ENUM]}</p>;
                } else {
                  const a = row as JobDtoAssigneeHistory;
                  if (!a.user?.name) {
                    contentVal = <p>Unassigned</p>;
                  } else {
                    contentVal = <p>{`${a.user.name} ${a.user.lastname} (${a.user.email})`}</p>;
                  }
                }

                return (
                  <Tr key={index}>
                    <Td>
                      <p>
                        {formatDateMsDsYHI(
                          currentTab === 'Status'
                            ? (row as JobStatusHistoryItemDto).date
                            : (row as JobDtoAssigneeHistory).createdAt,
                        )}
                      </p>
                    </Td>
                    <Td>{contentVal}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableWrapper>

        <OutlinedButton className="cancelBtn" onClick={onCancel}>
          Cancel
        </OutlinedButton>
      </Content>
    </ModalWrapper>
  );
};
